import { useEffect, useState } from 'preact/hooks';
import { listTraces, getTrace } from '../lib/api';
import { activeToken } from '../store/global';
import type { TraceSummary } from '../types/agno-api';
import { PageHeader } from '../components/PageHeader';
import { DataTable } from '../components/DataTable';
import { formatRelative, formatDuration, formatTokens, formatCost } from '../lib/format';

export function Traces() {
  const [items, setItems] = useState<TraceSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<TraceSummary | null>(null);

  const refresh = async () => {
    if (!activeToken.value) return;
    setLoading(true);
    try {
      const res = await listTraces();
      const data = Array.isArray(res) ? res : (res as { data: TraceSummary[] }).data || [];
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [activeToken.value]);

  const openDetail = async (id: string) => {
    const t = await getTrace(id).catch(() => null);
    if (t) setDetail(t);
  };

  return (
    <div class="flex h-full">
      <div class="flex-1 overflow-auto p-6">
        <div class="max-w-6xl">
          <PageHeader
            title="Traces"
            description="Histórico de execuções para debugging. Cada trace agrupa todos os spans de um run."
            icon={<i class="ph ph-pulse text-xl" />}
            actions={
              <button
                class="inline-flex items-center justify-center gap-2 h-8 px-4 rounded-md text-sm font-medium bg-background text-foreground border border-input shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:opacity-50 disabled:pointer-events-none"
                onClick={refresh}
                disabled={loading}
              >
                {loading ? 'Atualizando…' : 'Atualizar'}
              </button>
            }
          />
          <DataTable
            rows={items as unknown as Array<Record<string, unknown>>}
            rowKey={(r) => (r as unknown as TraceSummary).id}
            onRowClick={(r) => openDetail((r as unknown as TraceSummary).id)}
            columns={[
              { key: 'id', label: 'Trace', render: (r) => <code class="font-mono text-xs">{(r as unknown as TraceSummary).trace_id?.slice(0, 12) || (r as unknown as TraceSummary).id?.slice(0, 12)}…</code>, width: '16ch' },
              { key: 'session', label: 'Session', render: (r) => <code class="font-mono text-xs">{(r as unknown as TraceSummary).session_id?.slice(0, 12) || '—'}…</code>, width: '16ch' },
              {
                key: 'status',
                label: 'Status',
                width: '12ch',
                render: (r) => {
                  const s = (r as unknown as TraceSummary).status;
                  const tone = s === 'completed' ? 'text-success' : s === 'failed' ? 'text-destructive' : 'text-muted-foreground';
                  return <span class={`text-xs ${tone}`}>{s || '—'}</span>;
                },
              },
              { key: 'dur', label: 'Duração', render: (r) => <span class="text-xs">{formatDuration(((r as unknown as TraceSummary).duration_ms ?? 0) / 1000)}</span>, width: '10ch' },
              { key: 'tokens', label: 'Tokens', render: (r) => <span class="text-xs font-mono">{formatTokens((r as unknown as TraceSummary).total_tokens)}</span>, width: '10ch' },
              { key: 'cost', label: 'Custo', render: (r) => <span class="text-xs font-mono">{formatCost((r as unknown as TraceSummary).total_cost)}</span>, width: '10ch' },
              { key: 'created', label: 'Quando', render: (r) => <span class="text-xs">{formatRelative((r as unknown as TraceSummary).created_at)}</span>, width: '14ch' },
            ]}
            empty={
              activeToken.value
                ? 'Nenhum trace ainda. Execute uma conversa no chat para gerar traces.'
                : 'Configure o token nas configurações para ver os traces.'
            }
          />
        </div>
      </div>

      {detail && (
        <aside class="w-[28rem] border-l border-border bg-sidebar overflow-y-auto p-4">
          <div class="flex items-center justify-between mb-3">
            <h2 class="text-sm font-medium">Detalhe do trace</h2>
            <button
              class="inline-flex items-center justify-center size-8 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              onClick={() => setDetail(null)}
              title="Fechar"
              aria-label="Fechar"
            >
              <i class="ph ph-x text-base" />
            </button>
          </div>
          <pre class="text-[11px] font-mono whitespace-pre-wrap bg-muted border border-border rounded-md p-3 overflow-auto">
{JSON.stringify(detail, null, 2)}
          </pre>
        </aside>
      )}
    </div>
  );
}
